import React, { useState, useCallback } from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { PrimaryButton, SecondaryButton } from '../Button/Button';
import Modal from '../Modal/Modal';
import { H3 } from '../Heading/Heading';
import Icons from '../Icons/Icons';
import { useDispatch, useSelector } from 'react-redux';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import css from './InvitationModal.module.css';
import { fetUserDetailsUsingISDK, userDetailsSelector } from '../../ducks/user.duck';
import { isArrayLength } from '../../util/genericHelpers';
import { IconSpinner } from '..';
import {
  sendJobInvite,
  sendJobInviteSelector,
} from '../../containers/JobPostPage/JobPostPage.duck';
import { SendgridEmailTemplate } from '../../util/enums';
import { divisionOptions } from '../../config/configExtras';
const MAX_EMAIL_LIMIT = 50;

// Debounce function
const debounce = (func, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };
};

// Email validation regex
const validateEmail = input => input.includes('@');

const InvitationModal = ({ isOpen, onClose, formId, form, values, isInvitationModal }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { userDetails, fetchUserDetailsInProgress } = useSelector(userDetailsSelector);
  const { sendJobInviteInProgress } = useSelector(sendJobInviteSelector);
  const { recipientEntries = [], recipientEntrySearch } = values || {};
  const [suggestedEntries, setSuggestedEntries] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [error, setError] = useState('');

  const submitDisabled = recipientEntries?.length > MAX_EMAIL_LIMIT;

  // Debounced fetch function for suggestions
  const fetchSuggestions = useCallback(
    debounce(async term => {
      if (!term.trim()) {
        setError('Input cannot be empty.');
        setSuggestedEntries([]);
        return;
      }

      if (recipientEntries.some(entry => entry.value === term)) {
        setError('This entry has already been added.');
        return;
      }

      if (recipientEntries.length >= MAX_EMAIL_LIMIT) {
        setError(`You can only invite up to ${MAX_EMAIL_LIMIT} entries at a time.`);
        return;
      }

      try {
        // Determine the type of input
        const type = validateEmail(term) ? 'email' : 'text';

        // Call the API with the input and type
        const response = await dispatch(fetUserDetailsUsingISDK({ searchTerm: term, type }));
        // Process API response to generate suggested entries
        if (response?.email) {
          const {
            email,
            firstName,
            lastName,
            userId,
            agency,
            division,
            conference,
            highSchoolName,
          } = response;
          const currentDivision = divisionOptions.find(o => o.key === division)?.value;
          const namePart = [firstName, lastName].filter(Boolean).join(' ');
          const otherParts = [highSchoolName, currentDivision, conference]
            .filter(Boolean)
            .join(' - ');
          const suggestions = [
            {
              type: 'name',
              value: [namePart, otherParts].filter(Boolean).join(' - '),
              userId: userId || null,
            },
          ];

          setSuggestedEntries(suggestions);
          setError('');
        } else {
          setSuggestedEntries([]);
          setError('No matching user found.');
        }
      } catch (error) {
        setSuggestedEntries([]);
        setError('Error fetching user details.');
      }
    }, 1800), // Debounce delay of 2 seconds
    [dispatch, recipientEntries]
  );

  const addEntry = entry => {
    if (!entry?.value) return;

    if (selectedEntries.some(e => e.value === entry.value)) {
      setError('Duplicate entries are not allowed.');
      return;
    }

    setSelectedEntries([...selectedEntries, entry]);
    form.change('recipientEntries', [...selectedEntries, entry]);
    setSuggestedEntries([]);
    setError('');
  };

  const removeEntry = entryToRemove => {
    setSelectedEntries(selectedEntries.filter(entry => entry.value !== entryToRemove.value));
    form.change(
      'recipientEntries',
      selectedEntries.filter(entry => entry.value !== entryToRemove.value)
    );
  };

  const handleJobInviteSubmit = async () => {
    try {
      const emails = selectedEntries.filter(entry => entry.type === 'email').map(e => e.value);

      await dispatch(
        sendJobInvite({
          templateId: SendgridEmailTemplate.JOB_INVITE,
          recipientEmail: emails,
          templateData: {
            jobTitle: values?.title,
            jobLink: '',
          },
        })
      );
      onClose();
    } catch (error) {
      throw error;
    }
  };

  return (
    <Modal
      id="SendCredentialsModal"
      isOpen={isOpen}
      onClose={onClose}
      usePortal
      onManageDisableScrolling={(componentId, disableScrolling) =>
        dispatch(manageDisableScrolling(componentId, disableScrolling))
      }
      shouldShowIcon={<Icons name="userPlus" />}
      isInvitationModal={isInvitationModal}
    >
      <div className={css.invitationModalContent}>
        <div className={css.invitationModalHead}>
          <H3 as="h3">
            <FormattedMessage
              id="InvitationModal.sendCredentialsHeading"
              defaultMessage="Send Credentials"
            />
          </H3>
          <p>
            <FormattedMessage id="InvitationModal.sendCredentialsDescription" />
          </p>
        </div>

        <div className={css.sharedWithSec}>
          {/* Suggestions List */}
          {isArrayLength(suggestedEntries) && (
            <div className={css.suggestionsList}>
              {suggestedEntries.map((entry, index) => (
                <div key={index} className={css.suggestionItem} onClick={() => addEntry(entry)}>
                  {entry.value}
                </div>
              ))}
            </div>
          )}

          {/* Selected Entries List */}
          <div className={css.selectedEntriesList}>
            {selectedEntries.map((entry, index) => (
              <div key={index} className={css.entryPill}>
                <span>{entry.value}</span>
                <button className={css.remove} type="button" onClick={() => removeEntry(entry)}>
                  <Icons name="cross" />
                  <span>Remove</span>
                </button>
              </div>
            ))}
          </div>

          <Field name="recipientEntrySearch">
            {({ input }) => (
              <div className={css.inputContainer}>
                <span className={css.inputIcon}>
                  <Icons name="user" />
                </span>
                <input
                  {...input}
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'InvitationModal.inputPlaceholder',
                  })}
                  className={css.inputField}
                  onChange={e => {
                    input.onChange(e);
                    fetchSuggestions(e.target.value);
                  }}
                />
                {input.value && (
                  <button
                    type="button"
                    className={css.clearButton}
                    onClick={() => {
                      setError('');
                      input.onChange('');
                    }}
                  >
                    <Icons name="cross" />
                  </button>
                )}
              </div>
            )}
          </Field>

          {fetchUserDetailsInProgress && (
            <div className={css.loader}>
              <IconSpinner />
            </div>
          )}
          {error && <div className={css.errorMessage}>{error}</div>}
        </div>

        {/* Action Buttons */}
        <div className={css.actionButtons}>
          <SecondaryButton type="button" onClick={onClose}>
            <FormattedMessage id="InvitationModal.cancel" defaultMessage="Cancel" />
          </SecondaryButton>
          <PrimaryButton
            type="button"
            inProgress={sendJobInviteInProgress}
            onClick={handleJobInviteSubmit}
            disabled={submitDisabled}
          >
            <FormattedMessage id="InvitationModal.done" defaultMessage="Done" />
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default InvitationModal;
